/**
 * RemediationDraft — V101-5-4 / ADR-0054. The "Fix" section on a finding.
 *
 * Control-check findings only (any other kind renders nothing).
 *   open / acknowledged → interactive: draft on demand, use as plan, dismiss (held for Undo), redraft.
 *   remediated / closed / auto_closed → the accepted plan, read-only; nothing when no plan was ever accepted.
 * mode="dialog" (Operate → Remediate…): a static "Fix" section first; "Use as plan" is the dialog's submit.
 * mode="row" (Findings): collapsed, its state as a badge on the summary (one summary request for all rows).
 * The current plan is the most recently accepted draft; earlier accepted drafts are history.
 * GAC Pro never applies a fix. Hashes, check ids and model ids stay behind "Verify".
 *
 * Reads: GET findings/:id/remediation-drafts, GET ai-policy (any member).
 * Writes: POST draft / :draftId/accept / :draftId/dismiss, POST findings/:id/remediate (owner, admin,
 * editor), PUT ai-policy (owner: "Allow Workers AI"); others get AskAdmin.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Field, Spinner, Textarea } from '@aigovops/design-system';
import {
  decideRemediationDraft,
  draftRemediation,
  getAiPolicy,
  listRemediationDrafts,
  listWorkspaceMembers,
  putAiPolicy,
  remediationErrorCode,
  type AiPolicy,
  type AiProviderOffer,
  type RemediationDraft as Draft,
  type RemediationIac,
  type RemediationSummaryEntry,
} from '../lib/api';
import { remediateFinding } from '../lib/cert-ops-api';
import { federalOffAction, type ParityActionView } from '../lib/api';
import { canTakeAction, useFederalParity } from '../lib/federal-parity';
import { queryKeys } from '../lib/query-keys';
import { AskAdmin } from './AskAdmin';
import './remediation-draft.css';

const EDITOR_ROLES = new Set(['owner', 'admin', 'editor']);
const OPEN_STATUSES = new Set(['open', 'acknowledged']);
const COPY_RESET_MS = 2_000;

export interface RemediationFinding {
  finding_id: string;
  kind: string;
  status: string;
  title?: string | null;
  owner_user_id?: string | null;
}

/** Settings → Security #ai-providers; `returnTo` (a same-workspace path) brings the owner back after Save. */
export const aiProvidersHref = (prefix: string, returnTo?: string) =>
  `${prefix}/settings/security${returnTo ? `?return=${encodeURIComponent(returnTo)}` : ''}#ai-providers`;

/** Relative time ("3h ago"); the absolute timestamp goes in the element's title. */
export function ago(iso: string | null, now = Date.now()): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return 'unknown';
  const mins = Math.max(0, Math.round((now - t) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
const short = (iso: string | null) => ago(iso).replace(' ago', '').replace(' min', 'm');
const first = (name: string) => name.split(/\s+/)[0] || name;

/**
 * Which drafts show. `plan` = the most recently accepted draft (the current plan);
 * `current` = the newest draft or refusal when it is the newest row (newer than any plan);
 * `earlier` = older accepted drafts (history).
 */
export function pickDrafts(drafts: Draft[]): { current: Draft | null; plan: Draft | null; earlier: Draft[] } {
  const latest = drafts[0];
  const current = latest && (latest.status === 'draft' || latest.status === 'refused') ? latest : null;
  const accepted = drafts.filter((d) => d.status === 'accepted');
  return { current, plan: accepted[0] ?? null, earlier: accepted.slice(1) };
}

const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
/** Step indexes in `d` that the plan doesn't have (M5 compare). */
export function changedSteps(d: Draft, plan: Draft | null): Set<number> {
  if (!plan) return new Set();
  const had = new Set(plan.steps.map(norm));
  return new Set(d.steps.map((s, i) => (had.has(norm(s)) ? -1 : i)).filter((i) => i >= 0));
}
/** A new draft that says what the plan already says — no point replacing it. */
export function sameAsPlan(d: Draft | null, plan: Draft | null): boolean {
  if (!d || !plan || d.status !== 'draft') return false;
  return d.steps.length === plan.steps.length && changedSteps(d, plan).size === 0
    && JSON.stringify(d.iac ?? null) === JSON.stringify(plan.iac ?? null);
}

/** The provider an owner can allow in one click: in boundary first, then any that can draft. */
export function firstCapable(providers: AiProviderOffer[], policy: AiPolicy): AiProviderOffer | null {
  const candidates = providers.filter((p) => p.can_draft && !policy.allowed_providers.includes(p.provider_id)
    && (!policy.federal_boundary_only || p.boundary === 'in_boundary'));
  return candidates.find((p) => p.boundary === 'in_boundary') ?? candidates[0] ?? null;
}
export const allowLabel = (p: AiProviderOffer) => `Allow ${p.display_name}${p.boundary === 'in_boundary' ? ' (in boundary)' : ''}`;

const REFUSAL_LABEL: Record<string, string> = {
  no_hint: 'No reviewed guidance for this check yet',
  no_failing_check: 'No failing check to cite',
  low_confidence: 'Couldn’t draft confidently',
  invalid_output: 'Couldn’t draft confidently',
  provider_error: 'Couldn’t draft confidently',
};
const RETRYABLE = new Set(['low_confidence', 'invalid_output', 'provider_error']);

/** The collapsed-summary badge for a Findings row (one grammar: "Plan · Luis · 5d"). */
export function summaryBadge(s: RemediationSummaryEntry | null | undefined, nameOf: (id: string | null) => string): { text: string; plan: boolean } | null {
  if (!s) return null;
  if (s.status === 'accepted') return { text: `Plan · ${first(nameOf(s.decided_by_user_id))} · ${short(s.decided_at)}`, plan: true };
  if (s.status === 'draft') return s.plan_accepted ? { text: 'Plan · new draft', plan: true } : { text: 'Draft ready', plan: false };
  if (s.refusal_reason === 'unsupported_finding') return s.plan_accepted ? { text: 'Plan', plan: true } : null;
  if (s.plan_accepted) return { text: 'Plan', plan: true };
  return { text: s.refusal_reason === 'no_hint' ? 'No guidance yet' : 'Couldn’t draft', plan: false };
}

/** Assessor-reply display text: evidence hashes read as "evidence ref attached" (Copy keeps the full text). */
export function displayReply(reply: string, hash: string | null): string {
  let t = reply;
  if (hash) t = t.split(`evidence ${hash}`).join('evidence ref attached').split(hash).join('evidence ref attached');
  return t.replace(/(?:evidence\s+)?sha256:[0-9a-f]+(?:(?:…|\.\.\.)[0-9a-f]+)?/gi, 'evidence ref attached');
}

const VENDOR_DOCS: Array<[RegExp, string]> = [
  [/(^|\.)okta\.com$/, 'Okta docs'], [/(^|\.)aws\.amazon\.com$/, 'AWS docs'], [/(^|\.)github\.com$/, 'GitHub docs'],
  [/(^|\.)cloud\.google\.com$/, 'Google Cloud docs'], [/(^|\.)microsoft\.com$/, 'Microsoft docs'], [/(^|\.)google\.com$/, 'Google docs'],
  [/(^|\.)anthropic\.com$/, 'Anthropic docs'], [/(^|\.)openai\.com$/, 'OpenAI docs'], [/(^|\.)cloudflare\.com$/, 'Cloudflare docs'],
];
export function docLabel(url: string): string {
  let host = '';
  try { host = new URL(url).hostname.toLowerCase(); } catch { return 'Docs'; }
  for (const [re, label] of VENDOR_DOCS) if (re.test(host)) return label;
  return `${host.replace(/^(www|docs|help)\./, '')} docs`;
}

/** A spinner for inside a button: decorative (the button's text carries the state). */
export function ButtonSpinner() {
  return <span aria-hidden="true" className="rd-spin" />;
}

function errorFor(code: string | null): string {
  if (code === 'no_ai_provider') return 'No AI provider is allowed for this workspace.';
  if (code === 'rate_limited') return 'Drafts available again in about an hour.';
  if (code === 'not_open') return 'This finding is no longer open.';
  if (code === 'conflict') return 'Someone already decided on this draft.';
  return 'That did not go through. Try again.';
}

export function RemediationDraft(props: {
  slug: string; prefix: string; finding: RemediationFinding; role: string;
  mode?: 'dialog' | 'row';
  /** Row mode: this finding's entry from GET findings/remediation-summary. */
  summary?: RemediationSummaryEntry | null;
  /** Dialog: "Describe the fix" focuses the dialog's own note instead of the inline form. */
  onDescribe?: () => void;
  /** Dialog: a dismissed draft is on hold (Undo) — the dialog treats it as no draft. */
  onHold?: (draftId: string | null) => void;
}) {
  const { finding, summary, mode = 'row' } = props;
  if (finding.kind !== 'control_check_failure') return null;
  const open = OPEN_STATUSES.has(finding.status);
  // A closed finding keeps its plan visible; with the summary in hand, rows without a plan render nothing.
  if (!open && summary !== undefined && !summary?.plan_accepted) return null;
  return <FixSection {...props} mode={mode} readOnly={!open} canWrite={open && EDITOR_ROLES.has(props.role)} />;
}

function FixSection({ slug, prefix, finding, role, mode, summary, readOnly, canWrite, onDescribe, onHold }: {
  slug: string; prefix: string; finding: RemediationFinding; role: string; mode: 'dialog' | 'row'; summary?: RemediationSummaryEntry | null;
  readOnly: boolean; canWrite: boolean; onDescribe?: () => void; onHold?: (draftId: string | null) => void;
}) {
  const id = finding.finding_id;
  const base = `rd-${id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  const title = finding.title ?? 'this finding';
  const dialog = mode === 'dialog';
  const qc = useQueryClient();
  const location = useLocation();
  // Read-only closed findings without a summary fetch at once (to know whether a plan exists).
  const [open, setOpen] = useState(dialog || (readOnly && summary === undefined));
  const [expanded, setExpanded] = useState(dialog);
  const [busy, setBusy] = useState<null | 'draft' | 'accept' | 'allow' | 'fix'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [noProvider, setNoProvider] = useState(false);
  // ADR-0073 §7: a 409 federal_off carries the parity action (connect a provider the data may go to).
  const [federalOff, setFederalOff] = useState<ParityActionView | null>(null);
  const [pendingDismiss, setPendingDismiss] = useState<Draft | null>(null);
  const [showPlan, setShowPlan] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [note, setNote] = useState('');
  const [noteErr, setNoteErr] = useState('');
  const [live, setLive] = useState('');
  const [focusTo, setFocusTo] = useState<string | null>(null);
  const trigger = useRef<HTMLElement | null>(null);

  const q = useQuery({ queryKey: queryKeys.remediationDrafts(slug, id), queryFn: () => listRemediationDrafts(slug, id), enabled: open, retry: false, staleTime: 30_000 });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), enabled: open || !!summary, retry: false, staleTime: 60_000 });
  const policy = useQuery({ queryKey: queryKeys.aiPolicy(slug), queryFn: () => getAiPolicy(slug), enabled: open && canWrite, retry: false, staleTime: 60_000 });
  const nameOf = useCallback((uid: string | null) => members.data?.members.find((m) => m.user_id === uid)?.display_name || 'Someone', [members.data]);
  const policyBlocks = !!policy.data && !policy.data.providers.some((p) => p.can_draft && policy.data!.policy.allowed_providers.includes(p.provider_id));
  // Federal mode / the legacy in-boundary flag: the parity status says up front that the coach is off here.
  const parity = useFederalParity(open && canWrite ? slug : '');
  const coachParity = parity.data?.features.find((f) => f.feature === 'remediation_coach');
  const parityOff = !!parity.data && (parity.data.data_mode === 'federal' || parity.data.federal_boundary_only) && coachParity?.state === 'off' ? coachParity.action : null;
  const federalAction = federalOff ?? parityOff;
  const blocked = noProvider || policyBlocks || !!federalAction;

  const liveTimer = useRef<number | undefined>(undefined);
  const announce = (m: string) => { setLive(''); window.clearTimeout(liveTimer.current); liveTimer.current = window.setTimeout(() => setLive(m), 40); };
  const hush = () => { window.clearTimeout(liveTimer.current); setLive(''); };
  useEffect(() => () => window.clearTimeout(liveTimer.current), []);
  useEffect(() => {
    if (!focusTo) return;
    const el = document.getElementById(focusTo);
    if (el) el.focus();
    setFocusTo(null);
  }, [focusTo, q.data, pendingDismiss, err, describing, blocked]);

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.remediationDrafts(slug, id) }),
    qc.invalidateQueries({ queryKey: queryKeys.remediationSummary(slug) }),
    qc.invalidateQueries({ queryKey: queryKeys.findings(slug) }),
  ]);
  // Focus follows only when it is still on the trigger (or nowhere).
  const focusIfIdle = (target: string) => {
    const a = document.activeElement;
    if (!a || a === document.body || a === trigger.current) setFocusTo(target);
  };
  // M4: errors speak through their Alert (role=alert) only; focus follows when idle.
  const fail = (e: unknown) => {
    const code = remediationErrorCode(e);
    if (code === 'rate_limited') setRateLimited(true);
    if (code === 'no_ai_provider') { setNoProvider(true); setErr(null); focusIfIdle(`${base}-blocked`); return code; }
    const off = federalOffAction(e);
    if (off) { setFederalOff(off); setErr(null); focusIfIdle(`${base}-blocked`); return 'federal_off'; }
    setErr(errorFor(code));
    focusIfIdle(`${base}-err`);
    return code;
  };

  // M3: a dismiss is held (Undo) until the next action, unmount, or pagehide — no countdown.
  const held = useRef<Draft | null>(null);
  const commitDismiss = useCallback(async (keepalive = false) => {
    const d = held.current;
    if (!d) return;
    held.current = null;
    try { await decideRemediationDraft(slug, id, d.draft_id, 'dismiss', keepalive ? { keepalive: true } : undefined); } catch { /* already decided — the refresh shows the truth */ }
    if (keepalive) return;
    setPendingDismiss(null);
    onHold?.(null);
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.remediationDrafts(slug, id) }),
      qc.invalidateQueries({ queryKey: queryKeys.remediationSummary(slug) }),
    ]);
  }, [qc, slug, id, onHold]);
  useEffect(() => {
    const onHide = () => { void commitDismiss(true); };
    window.addEventListener('pagehide', onHide);
    return () => { window.removeEventListener('pagehide', onHide); void commitDismiss(); };
  }, [commitDismiss]);

  const runDraft = async (el: HTMLElement | null) => {
    if (busy || rateLimited || blocked) return;
    trigger.current = el;
    setBusy('draft'); setErr(null); announce('Drafting a fix…');
    try {
      await commitDismiss();
      const { draft } = await draftRemediation(slug, id);
      await refresh();
      announce(draft.status === 'draft' ? 'Draft ready' : REFUSAL_LABEL[draft.refusal_reason ?? ''] ?? 'Couldn’t draft confidently');
      focusIfIdle(`${base}-new`);
    } catch (e) {
      hush();
      if (fail(e) === 'not_open') await refresh();
    } finally { setBusy(null); }
  };
  const accept = async (d: Draft, el: HTMLElement | null) => {
    if (busy) return;
    trigger.current = el;
    setBusy('accept'); setErr(null);
    try {
      await decideRemediationDraft(slug, id, d.draft_id, 'accept');
      await refresh();
      announce('Saved as the plan');
      focusIfIdle(`${base}-plan`);
    } catch (e) {
      const code = fail(e);
      if (code === 'conflict' || code === 'not_open') await refresh();
    } finally { setBusy(null); }
  };
  const dismiss = (d: Draft) => {
    if (busy) return;
    void commitDismiss();
    held.current = d;
    setPendingDismiss(d);
    onHold?.(d.draft_id);
    announce('Draft dismissed');
    setFocusTo(`${base}-undo`);
  };
  const undo = () => {
    held.current = null;
    setPendingDismiss(null);
    onHold?.(null);
    announce('Draft restored');
    setFocusTo(`${base}-new`);
  };
  // H2: the owner allows a capable provider without leaving the finding.
  const allow = async (p: AiProviderOffer, el: HTMLElement | null) => {
    if (busy || !policy.data) return;
    trigger.current = el;
    setBusy('allow');
    try {
      const cur = policy.data.policy;
      const r = await putAiPolicy(slug, { allowed_providers: [...cur.allowed_providers, p.provider_id], default_provider_id: cur.default_provider_id, federal_boundary_only: cur.federal_boundary_only });
      qc.setQueryData(queryKeys.aiPolicy(slug), { policy: r.policy, providers: r.providers });
      setNoProvider(false); setErr(null);
      announce(`${p.display_name} allowed`);
      setFocusTo(`${base}-draft`);
    } catch (e) {
      // Shown inside the blocked note (the section is still blocked), and focus goes there.
      setErr(e instanceof Error && e.message ? `Couldn't allow ${p.display_name}: ${e.message}` : `Couldn't allow ${p.display_name}.`);
      focusIfIdle(`${base}-blocked`);
    } finally { setBusy(null); }
  };
  // B1: "Describe the fix" — record how it was fixed right here (same write as Operate's "I fixed it").
  const markFixed = async () => {
    if (busy) return;
    if (note.trim().length < 10) { setNoteErr('Say how it was fixed (at least 10 characters).'); setFocusTo(`${base}-note`); return; }
    setBusy('fix'); setNoteErr('');
    try {
      await remediateFinding(slug, id, note.trim());
      announce('Marked fixed · closes when the check passes');
      setDescribing(false); setNote('');
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.findings(slug) }), qc.invalidateQueries({ queryKey: queryKeys.remediationSummary(slug) })]);
    } catch (e) {
      setNoteErr(e instanceof Error && e.message ? e.message : 'That did not go through.');
      setFocusTo(`${base}-note`);
    } finally { setBusy(null); }
  };

  const { current: rawCurrent, plan, earlier } = pickDrafts(q.data?.drafts ?? []);
  const current = pendingDismiss && rawCurrent?.draft_id === pendingDismiss.draft_id ? null : rawCurrent;
  if (q.data && !plan && (readOnly || (current?.refusal_reason === 'unsupported_finding'))) return null;

  const H = dialog ? 'h4' : 'p';
  const isOwner = role === 'owner';
  const editorish = EDITOR_ROLES.has(role);
  const askEditor = <AskAdmin slug={slug} single lead="An editor" what="drafts fixes" roles={['owner', 'admin', 'editor']} prefer={finding.owner_user_id}
    subject={`Draft a fix: ${title}`} />;
  const draftBtn = (label: string, variant: 'primary' | 'secondary' | 'ghost', which: 'draft' | 'redraft') => {
    if (!canWrite || blocked) return null;
    const off = !!busy || rateLimited;
    return (
      <Button key={which} id={`${base}-${which}`} variant={variant} compact aria-disabled={off || undefined}
        aria-describedby={rateLimited ? `${base}-err` : undefined} onClick={(e) => void runDraft(e.currentTarget)}>
        {busy === 'draft' ? <><ButtonSpinner /><span>Drafting…</span></> : label}
      </Button>
    );
  };
  const candidate = policy.data ? firstCapable(policy.data.providers, policy.data.policy) : null;
  const here = `${location.pathname}${location.search}`;

  const blockedNote = blocked && canWrite && federalAction ? (
    <div id={`${base}-blocked`} tabIndex={-1} className="rd-err">
      <Alert variant="warning">
        <p>This workspace's data can't go to the AI providers allowed here.</p>
        {canTakeAction(role, federalAction)
          ? <span className="rd-actions"><Link className="rd-link" to={federalAction.href}>{federalAction.label}</Link></span>
          : <AskAdmin slug={slug} compact what="connects an AI provider for this workspace's data" subject="Connect an AI provider for drafted fixes" />}
      </Alert>
    </div>
  ) : blocked && canWrite ? (
    <div id={`${base}-blocked`} tabIndex={-1} className="rd-err">
      <Alert variant="warning">
        <p>No AI provider is allowed for this workspace.</p>
        {err ? <p className="rd-err-inline">{err}</p> : null}
        {isOwner ? (
          <span className="rd-actions">
            {candidate ? <Button variant="secondary" compact aria-disabled={busy === 'allow' || undefined} onClick={(e) => void allow(candidate, e.currentTarget)}>
              {busy === 'allow' ? <><ButtonSpinner /><span>Saving…</span></> : allowLabel(candidate)}</Button> : null}
            <Link className="rd-link" to={aiProvidersHref(prefix, here)}>{candidate ? 'Other providers' : 'AI providers'}</Link>
          </span>
        ) : <AskAdmin slug={slug} compact roles={['owner']} lead="A workspace owner" what="allows AI providers" verb="allow an AI provider" subject="Allow an AI provider for drafted fixes" />}
      </Alert>
    </div>
  ) : null;

  const describeForm = describing ? (
    <div className="rd-describe" role="group" aria-labelledby={`${base}-note-l`}>
      <Field id={`${base}-note`} label="How it was fixed" required error={noteErr || undefined}>
        <Textarea rows={3} maxLength={2000} value={note} onChange={(e) => { setNote(e.currentTarget.value); setNoteErr(''); }} />
      </Field>
      <span id={`${base}-note-l`} className="sr-only">{`Describe the fix · ${title}`}</span>
      <div className="rd-actions">
        <Button variant="primary" compact aria-disabled={busy === 'fix' || undefined} onClick={() => void markFixed()}>{busy === 'fix' ? <><ButtonSpinner /><span>Saving…</span></> : 'Mark fixed'}</Button>
        <Button variant="ghost" compact onClick={() => { setDescribing(false); setNoteErr(''); setFocusTo(`${base}-describe`); }}>Cancel</Button>
      </div>
    </div>
  ) : null;

  let body: ReactNode;
  if (q.isLoading) body = <Spinner size={16} label="Loading the fix" />;
  else if (q.error) body = <Alert variant="error"><p>The fix could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>;
  else {
    const newer = !!current && current.status === 'draft';
    const planCollapsed = !!plan && newer && !showPlan;
    const planBlock = plan ? (
      <div className="rd-plan" role="group" aria-labelledby={`${base}-plan`}>
        <div className="rd-state">
          <H className="rd-state-t" id={`${base}-plan`} tabIndex={-1}>
            <span>Plan</span><span className="rd-dim" title={plan.decided_at ?? undefined}>{` · ${nameOf(plan.decided_by_user_id)} · ${ago(plan.decided_at)}`}</span>
          </H>
          {!readOnly && !dialog ? <Badge>Closes when the check passes</Badge> : null}
          {!plan.hint_reviewed ? <Badge>Guidance pending review</Badge> : null}
          {newer ? (
            <Button variant="ghost" compact aria-expanded={showPlan} aria-controls={`${base}-planbody`} onClick={() => setShowPlan((x) => !x)}>{showPlan ? 'Hide plan' : 'Show plan'}</Button>
          ) : null}
        </div>
        {!planCollapsed ? (
          <div id={`${base}-planbody`} className="rd-stack">
            <DraftBody d={plan} base={`${base}-p`} title={title} which="plan" />
            {earlier.length ? (
              <details className="rd-verify rd-earlier">
                <summary>{`Earlier plans (${earlier.length})`}</summary>
                <ul className="rd-earlier-list">
                  {earlier.map((e) => (
                    <li key={e.draft_id}>
                      <span className="rd-dim" title={e.decided_at ?? undefined}>{`${nameOf(e.decided_by_user_id)} · ${ago(e.decided_at)}`}</span>
                      <ol className="rd-steps">{e.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}
        {!readOnly && !current && !pendingDismiss && canWrite ? <div className="rd-actions">{draftBtn('Redraft', 'ghost', 'redraft')}</div> : null}
      </div>
    ) : null;

    let next: ReactNode = null;
    if (readOnly) next = null;
    else if (pendingDismiss) {
      next = (
        <div className="rd-actions" role="group" aria-label={`Dismissed draft · ${title}`}>
          {!plan ? draftBtn('Draft a fix', 'primary', 'draft') : draftBtn('Redraft', 'ghost', 'redraft')}
          <span className="rd-dim">Draft dismissed</span>
          <Button id={`${base}-undo`} variant="ghost" compact onClick={undo}>Undo</Button>
        </div>
      );
    } else if (current?.status === 'draft') {
      const same = sameAsPlan(current, plan);
      const changed = changedSteps(current, plan);
      next = (
        <div className="rd-draft" role="group" aria-labelledby={`${base}-new`}>
          <div className="rd-state">
            <H className="rd-state-t" id={`${base}-new`} tabIndex={-1}>{plan ? 'New draft' : 'Draft'}</H>
            {same ? <Badge>Same as current plan</Badge> : null}
            {!current.hint_reviewed ? <Badge>Guidance pending review</Badge> : null}
          </div>
          {plan && canWrite ? (
            <div className="rd-actions">
              <Button variant="secondary" compact aria-disabled={!!busy || undefined} onClick={() => dismiss(current)}>Keep current plan</Button>
            </div>
          ) : null}
          <DraftBody d={current} base={`${base}-d`} title={title} which={plan ? 'new draft' : 'draft'} changed={changed} />
          {canWrite ? (
            <div className="rd-actions">
              {!dialog && !same ? <Button variant="primary" compact aria-disabled={!!busy || undefined} onClick={(e) => void accept(current, e.currentTarget)}>
                {busy === 'accept' ? <><ButtonSpinner /><span>Saving…</span></> : plan ? 'Replace plan' : 'Use as plan'}</Button> : null}
              {!plan ? <Button variant="secondary" compact aria-disabled={!!busy || undefined} onClick={() => dismiss(current)}>Dismiss</Button> : null}
              {draftBtn('Redraft', 'ghost', 'redraft')}
            </div>
          ) : askEditor}
        </div>
      );
    } else if (current?.status === 'refused') {
      const reason = current.refusal_reason ?? 'provider_error';
      const describeAction = reason === 'no_hint' && editorish ? (
        <Button id={`${base}-describe`} variant="secondary" compact aria-expanded={dialog ? undefined : describing}
          onClick={() => { if (onDescribe) onDescribe(); else { setDescribing(true); setFocusTo(`${base}-note`); } }}>Describe the fix</Button>
      ) : null;
      next = (
        <div className="rd-draft" role="group" aria-labelledby={`${base}-new`}>
          <div className="rd-state"><H className="rd-state-t" id={`${base}-new`} tabIndex={-1}>{REFUSAL_LABEL[reason] ?? 'Couldn’t draft confidently'}</H></div>
          {!describing ? (
            <div className="rd-actions">
              {describeAction}
              {reason === 'no_failing_check' ? <Link className="rd-link" to={`${prefix}/setup#security-posture`}>Check connections</Link> : null}
              {RETRYABLE.has(reason) ? draftBtn('Redraft', 'secondary', 'redraft') : null}
              {reason === 'no_failing_check' ? draftBtn('Redraft', 'ghost', 'redraft') : null}
              {current.doc_url ? <DocLink url={current.doc_url} title={title} /> : null}
            </div>
          ) : describeForm}
          {!editorish ? <AskAdmin slug={slug} single lead="An editor" what={reason === 'no_hint' ? 'records the fix' : 'drafts fixes'}
            roles={['owner', 'admin', 'editor']} prefer={finding.owner_user_id} subject={`Fix: ${title}`} /> : null}
        </div>
      );
    } else if (!plan) {
      next = canWrite ? <div className="rd-actions">{draftBtn('Draft a fix', 'primary', 'draft')}</div> : askEditor;
    }
    body = (
      <>
        {err && !blocked ? (
          <div id={`${base}-err`} tabIndex={-1} className="rd-err"><Alert variant={rateLimited ? 'warning' : 'error'}><p>{err}</p></Alert></div>
        ) : null}
        {blockedNote}{planBlock}{next}
      </>
    );
  }

  const badge = !dialog && !expanded ? summaryBadge(summary, nameOf) : null;
  const liveNode = <p role="status" aria-live="polite" className="sr-only">{live}</p>;
  if (dialog) {
    return (
      <div className="rd rd--dialog" data-part="remediation-draft" role="group" aria-labelledby={`${base}-fix`}>
        {liveNode}
        <h3 id={`${base}-fix`} className="rd-h">Fix</h3>
        <div className="rd-body">{body}</div>
      </div>
    );
  }
  return (
    <div className="rd" data-part="remediation-draft">
      {liveNode}
      <details className="rd-details" onToggle={(e) => { const isOpen = (e.currentTarget as HTMLDetailsElement).open; setExpanded(isOpen); setOpen((x) => x || isOpen); }}>
        <summary className="rd-summary">
          <span>Fix</span>
          {badge ? <span className={`rd-badge${badge.plan ? ' rd-badge--plan' : ''}`}>{badge.text}</span> : null}
          <span className="sr-only">{` · ${title}`}</span>
        </summary>
        {open ? <div className="rd-body">{body}</div> : null}
      </details>
    </div>
  );
}

function DocLink({ url, title }: { url: string; title: string }) {
  return <a className="rd-link" href={url} target="_blank" rel="noopener noreferrer">{docLabel(url)}<span className="sr-only">{` · ${title} (opens in a new tab)`}</span></a>;
}

function CopyButton({ text, what, title }: { text: string; what: string; title: string }) {
  const [state, setState] = useState<'' | 'ok' | 'fail'>('');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = () => {
    setState('');
    window.clearTimeout(timer.current);
    void (async () => {
      let ok = true;
      try { await navigator.clipboard.writeText(text); } catch { ok = false; }
      window.setTimeout(() => setState(ok ? 'ok' : 'fail'), 30);
      timer.current = window.setTimeout(() => setState(''), COPY_RESET_MS);
    })();
  };
  return (
    <span className="rd-copy">
      {state === 'fail' ? <span className="rd-copy-fail">Copy unavailable — select the text</span> : null}
      <Button variant="ghost" compact onClick={copy}>{state === 'ok' ? 'Copied' : 'Copy'}<span className="sr-only">{` ${what} · ${title}`}</span></Button>
      <span role="status" className="sr-only">{state === 'ok' ? `${what} copied` : state === 'fail' ? 'Copy unavailable' : ''}</span>
    </span>
  );
}

/** A code block that shows a right-edge fade while it overflows. */
function CodeBlock({ code, label }: { code: string; label: string }) {
  const ref = useRef<HTMLPreElement>(null);
  const [over, setOver] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOver(el.scrollWidth - el.scrollLeft - el.clientWidth > 2);
    check();
    el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => { el.removeEventListener('scroll', check); window.removeEventListener('resize', check); };
  }, [code]);
  return (
    <div className="rd-pre-wrap" data-overflow={over || undefined}>
      <pre ref={ref} className="rd-pre" role="group" aria-label={label} tabIndex={0}><code>{code}</code></pre>
    </div>
  );
}

function Block({ id, label, copy, title, children }: { id: string; label: string; copy: string; title: string; children: ReactNode }) {
  return (
    <div className="rd-block" role="group" aria-labelledby={id}>
      <div className="rd-block-h"><span id={id} className="rd-block-l">{label}</span><CopyButton text={copy} what={label} title={title} /></div>
      {children}
    </div>
  );
}

function DraftBody({ d, base, title, which, changed }: { d: Draft; base: string; title: string; which: string; changed?: Set<number> }) {
  const iac: RemediationIac = d.iac ?? {};
  return (
    <>
      {d.steps.length ? (
        <ol className="rd-steps" aria-label={`Steps · ${which} · ${title}`}>
          {d.steps.map((s, i) => (
            <li key={i}>{s}{changed?.has(i) ? <> <Badge>Changed</Badge><span className="sr-only"> from the current plan</span></> : null}</li>
          ))}
        </ol>
      ) : null}
      {iac.terraform ? (
        <Block id={`${base}-tf`} label="Terraform" copy={iac.terraform} title={title}><CodeBlock code={iac.terraform} label={`Terraform · ${which} · ${title}`} /></Block>
      ) : null}
      {iac.aws_config_rule ? <Block id={`${base}-aws`} label="AWS Config rule" copy={iac.aws_config_rule} title={title}><code className="rd-chip">{iac.aws_config_rule}</code></Block> : null}
      {iac.gcp_constraint ? <Block id={`${base}-gcp`} label="GCP constraint" copy={iac.gcp_constraint} title={title}><code className="rd-chip">{iac.gcp_constraint}</code></Block> : null}
      {d.assessor_reply ? (
        <Block id={`${base}-reply`} label="Assessor reply" copy={d.assessor_reply} title={title}><p className="rd-reply">{displayReply(d.assessor_reply, d.evidence_hash)}</p></Block>
      ) : null}
      <div className="rd-prov">
        <span title={d.created_at}>{`Based on the failing check · drafted ${ago(d.created_at)}`}</span>
        {d.doc_url ? <DocLink url={d.doc_url} title={title} /> : null}
        <details className="rd-verify">
          <summary>Verify<span className="sr-only">{` · ${which} · ${title}`}</span></summary>
          <dl className="rd-dl">
            {d.evidence_hash ? <><dt>Evidence hash</dt><dd><code>{d.evidence_hash}</code></dd></> : null}
            {d.check_id ? <><dt>Check</dt><dd><code>{d.check_id}</code>{d.check_key ? <> · <code>{d.check_key}</code></> : null}</dd></> : null}
            {d.hint_version != null ? <><dt>Guidance</dt><dd>{`v${d.hint_version}${d.hint_reviewed ? ' · reviewed' : ' · pending review'}`}</dd></> : null}
            {d.model ? <><dt>Model</dt><dd><code>{d.provider_id ? `${d.provider_id} · ${d.model}` : d.model}</code></dd></> : null}
            {d.confidence != null ? <><dt>Confidence</dt><dd>{`${Math.round(d.confidence * 100)}%`}</dd></> : null}
            {iac.mappings?.length ? <><dt>Policy templates</dt><dd>{iac.mappings.map((m) => `${m.target_type} ${m.mapping_version}`).join(' · ')}</dd></> : null}
          </dl>
        </details>
      </div>
    </>
  );
}
